// src/features/auth/token-management/storage/MemoryCache.ts

export interface CacheItem<T> {
  value: T;
  expiresAt?: number;
  createdAt: number;
  accessCount: number;
  lastAccessed: number;
}

export interface MemoryCacheConfig {
  maxSize?: number;
  defaultTTL?: number; // milliseconds
  cleanupInterval?: number; // milliseconds
  onEvict?: <T>(key: string, value: T) => void;
}

export class MemoryCache<T> {
  private cache: Map<string, CacheItem<T>>;
  private config: Required<MemoryCacheConfig>;
  private cleanupTimer?: ReturnType<typeof setInterval>;
  private accessOrder: string[] = [];

  constructor(config: MemoryCacheConfig = {}) {
    this.cache = new Map();
    this.config = {
      maxSize: 100,
      defaultTTL: 3600000, // 1 hour
      cleanupInterval: 60000, // 1 minute
      onEvict: () => {},
      ...config,
    };

    // Start cleanup timer
    if (this.config.cleanupInterval > 0) {
      this.startCleanupTimer();
    }
  }

  /**
   * Set a value in the cache
   */
  set(key: string, value: T, ttl?: number): void {
    // Check if we need to evict items
    if (this.cache.size >= this.config.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }

    const expiresAt = ttl !== undefined 
      ? Date.now() + ttl 
      : ttl === null 
        ? undefined 
        : Date.now() + this.config.defaultTTL;

    const cacheItem: CacheItem<T> = {
      value,
      expiresAt,
      createdAt: Date.now(),
      accessCount: 0,
      lastAccessed: Date.now(),
    };

    this.cache.set(key, cacheItem);
    this.updateAccessOrder(key);
  }

  /**
   * Get a value from the cache
   */
  get(key: string): T | undefined {
    const item = this.cache.get(key);
    
    if (!item) {
      return undefined;
    }

    // Check if expired
    if (item.expiresAt && Date.now() > item.expiresAt) {
      this.delete(key);
      return undefined;
    }

    // Update access info
    item.accessCount++;
    item.lastAccessed = Date.now();
    this.updateAccessOrder(key);

    return item.value;
  }

  /**
   * Check if a key exists in the cache
   */
  has(key: string): boolean {
    const item = this.cache.get(key);
    
    if (!item) {
      return false;
    }

    // Check if expired
    if (item.expiresAt && Date.now() > item.expiresAt) {
      this.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Delete a key from the cache
   */
  delete(key: string): boolean {
    const item = this.cache.get(key);
    
    if (item) {
      this.config.onEvict(key, item.value);
      this.cache.delete(key);
      this.removeFromAccessOrder(key);
      return true;
    }

    return false;
  }

  /**
   * Clear all items from the cache
   */
  clear(): void {
    // Call onEvict for each item
    this.cache.forEach((item, key) => {
      this.config.onEvict(key, item.value);
    });

    this.cache.clear();
    this.accessOrder = [];
  }

  /**
   * Get the size of the cache
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Get all keys in the cache
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get all values in the cache
   */
  values(): T[] {
    const values: T[] = [];
    
    this.cache.forEach((item) => {
      if (!item.expiresAt || Date.now() <= item.expiresAt) {
        values.push(item.value);
      }
    });

    return values;
  }

  /**
   * Get all entries in the cache
   */
  entries(): Array<[string, T]> {
    const entries: Array<[string, T]> = [];
    
    this.cache.forEach((item, key) => {
      if (!item.expiresAt || Date.now() <= item.expiresAt) {
        entries.push([key, item.value]);
      }
    });

    return entries;
  }

  /**
   * Update TTL for a key
   */
  updateTTL(key: string, ttl: number): boolean {
    const item = this.cache.get(key);
    
    if (!item) {
      return false;
    }

    item.expiresAt = Date.now() + ttl;
    return true;
  }

  /**
   * Get metadata for a cache item
   */
  getMetadata(key: string): Omit<CacheItem<T>, 'value'> | undefined {
    const item = this.cache.get(key);
    
    if (!item) {
      return undefined;
    }

    return {
      expiresAt: item.expiresAt,
      createdAt: item.createdAt,
      accessCount: item.accessCount,
      lastAccessed: item.lastAccessed,
    };
  }

  /**
   * Cleanup expired items
   */
  cleanup(): number {
    let cleaned = 0;
    const now = Date.now();
    
    this.cache.forEach((item, key) => {
      if (item.expiresAt && now > item.expiresAt) {
        this.delete(key);
        cleaned++;
      }
    });

    return cleaned;
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
    evictionCount: number;
    oldestItem?: { key: string; age: number };
    mostAccessed?: { key: string; count: number };
  } {
    let totalAccess = 0;
    let oldestItem: { key: string; age: number } | undefined;
    let mostAccessed: { key: string; count: number } | undefined;
    const now = Date.now();

    this.cache.forEach((item, key) => {
      totalAccess += item.accessCount;

      const age = now - item.createdAt;
      if (!oldestItem || age > oldestItem.age) {
        oldestItem = { key, age };
      }

      if (!mostAccessed || item.accessCount > mostAccessed.count) {
        mostAccessed = { key, count: item.accessCount };
      }
    });

    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      hitRate: this.cache.size > 0 ? totalAccess / this.cache.size : 0,
      evictionCount: 0, // Would need to track this
      oldestItem,
      mostAccessed,
    };
  }

  /**
   * Start cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Stop cleanup timer
   */
  private stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
  }

  /**
   * Evict least recently used item
   */
  private evictLRU(): void {
    if (this.accessOrder.length === 0) {
      return;
    }

    // Remove least recently used
    const lruKey = this.accessOrder[0];
    this.delete(lruKey);
  }

  /**
   * Update access order for LRU
   */
  private updateAccessOrder(key: string): void {
    this.removeFromAccessOrder(key);
    this.accessOrder.push(key);
  }

  /**
   * Remove key from access order
   */
  private removeFromAccessOrder(key: string): void {
    const index = this.accessOrder.indexOf(key);
    if (index !== -1) {
      this.accessOrder.splice(index, 1);
    }
  }

  /**
   * Destroy cache and cleanup resources
   */
  destroy(): void {
    this.stopCleanupTimer();
    this.clear();
  }
}